import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Panel, StatusChip, type StatusTone } from '../../ui-kit';

export interface VoiceAgentCard {
  name: string;
  voice: string | null;
  voice_style: string | null;
  /** `live` only when the provider really has the agent. */
  status: 'live' | 'missing' | 'unknown';
  /** What it says first, by kind of call: "First call", "A follow-up call", "Every call". */
  lines: { label: string; line: string }[];
}

const STATUS: Record<VoiceAgentCard['status'], { tone: StatusTone; label: string }> = {
  live: { tone: 'good', label: 'Live' },
  missing: { tone: 'bad', label: 'Not set up' },
  unknown: { tone: 'neutral', label: 'Could not check' },
};

/**
 * Every agent's voice agents, the same way on each configuration page: who
 * each is, checked against the provider, what it says first, and how its
 * calls go. The list is how you choose; the detail is what you chose.
 */
export default function VoiceAgents({
  agents, facts, providerError, loading,
}: {
  agents: VoiceAgentCard[];
  facts: { label: string; value: string }[];
  providerError: string | null | undefined;
  loading: boolean;
}) {
  const [chosenName, setChosenName] = React.useState<string | null>(null);
  const chosen = agents.find((a) => a.name === chosenName) ?? agents[0];
  const count = agents.length === 1 ? '1 AI agent' : `${agents.length} AI agents, one job`;

  return (
    <Panel
      title="Voice agents"
      subtitle={providerError
        ? `${count} · ${providerError}, so their status could not be checked`
        : `${count} · checked against ElevenLabs`}
      loading={loading}
      loadingHeight={260}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2 }}>
        <Stack
          spacing={0.5}
          sx={(t) => ({
            width: { xs: '100%', md: 240 },
            flexShrink: 0,
            pr: { md: 2 },
            borderRight: { md: `1px solid ${t.tokens.border.subtle}` },
          })}
        >
          {agents.map((agent) => {
            const selected = agent.name === chosen?.name;
            return (
              <Stack
                key={agent.name}
                component="button"
                type="button"
                onClick={() => setChosenName(agent.name)}
                spacing={0.25}
                sx={(t) => ({
                  all: 'unset',
                  boxSizing: 'border-box',
                  px: 1.25,
                  py: 1,
                  cursor: 'pointer',
                  borderRadius: `${t.ds.radius.md}px`,
                  border: `1px solid ${selected ? t.tokens.brand.primary : 'transparent'}`,
                  bgcolor: selected ? t.tokens.brand.primarySoft : 'transparent',
                  '&:hover': { bgcolor: selected ? t.tokens.brand.primarySoft : t.tokens.surface.sunken },
                  '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing },
                })}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{agent.name}</Typography>
                  <StatusChip {...STATUS[agent.status]} />
                </Stack>
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                  {agent.voice ? [agent.voice, agent.voice_style].filter(Boolean).join(' · ') : 'No voice set'}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {chosen ? (
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary })}>{chosen.name}</Typography>
            {chosen.lines.map((said) => <Said key={said.label} label={said.label} line={said.line} />)}
            <Stack spacing={0.75}>
              {facts.map((fact) => (
                <Stack key={fact.label} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, flexShrink: 0 })}>
                    {fact.label}
                  </Typography>
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right' })}>
                    {fact.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Panel>
  );
}

/** A line the agent says, quoted as it is said. */
function Said({ label, line }: { label: string; line: string }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{label}</Typography>
      <Typography
        variant="body2"
        sx={(t) => ({
          color: t.tokens.text.primary,
          p: 1.25,
          borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.surface.sunken,
        })}
      >
        “{line}”
      </Typography>
    </Stack>
  );
}
